import { useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import type { ProjectTypeProfile } from '../../../core/types/data-models';
import type { OnboardingDraft, OnboardingUrlField } from './useOnboardingDraft';

const URL_FIELDS: { key: OnboardingUrlField; label: string; hint: string }[] = [
  { key: 'websiteUrl', label: 'Website', hint: 'Landing page, product site or app-store listing.' },
  { key: 'githubUrl', label: 'GitHub repository', hint: 'Public repository of the product.' },
  { key: 'docsUrl', label: 'Documentation portal', hint: 'Public docs, help center or API reference.' },
  { key: 'linkedinUrl', label: 'LinkedIn page', hint: 'Company or product page.' },
];

/** Adds `https://` when the user typed a bare host such as `acme.com`. */
export function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(normalizeUrl(value));
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}

function visibleFields(profile: ProjectTypeProfile) {
  return URL_FIELDS.filter((f) => profile.inputs[f.key] !== 'hidden');
}

function fieldError(value: string, required: boolean): string | null {
  if (!value.trim()) return required ? 'Required' : null;
  return isValidHttpUrl(value) ? null : 'Enter a full address such as https://example.com';
}

export function isSourcesStepValid(draft: OnboardingDraft, profile: ProjectTypeProfile): boolean {
  return visibleFields(profile).every(
    (f) => fieldError(draft[f.key], profile.inputs[f.key] === 'required') === null,
  );
}

interface SourcesStepProps {
  draft: OnboardingDraft;
  onChange: (patch: Partial<OnboardingDraft>) => void;
  profile: ProjectTypeProfile;
  heading: string;
  helper: string;
}

/**
 * URL fields driven by `profile.inputs`: which of website / GitHub / docs /
 * LinkedIn are shown, and which are required. Errors appear once a field has
 * been left, and a bare host is completed to `https://…` on blur.
 */
export function SourcesStep({ draft, onChange, profile, heading, helper }: SourcesStepProps) {
  const [touched, setTouched] = useState<Partial<Record<OnboardingUrlField, boolean>>>({});
  const fields = visibleFields(profile);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" component="h2" fontWeight={700}>
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helper}
        </Typography>
      </Box>

      {fields.map((f, i) => {
        const required = profile.inputs[f.key] === 'required';
        const error = touched[f.key] ? fieldError(draft[f.key], required) : null;
        return (
          <TextField
            key={f.key}
            label={f.label}
            required={required}
            fullWidth
            type="url"
            inputMode="url"
            autoComplete="url"
            autoFocus={i === 0}
            placeholder="https://"
            value={draft[f.key]}
            onChange={(e) => onChange({ [f.key]: e.target.value } as Partial<OnboardingDraft>)}
            onBlur={() => {
              setTouched((t) => ({ ...t, [f.key]: true }));
              onChange({ [f.key]: normalizeUrl(draft[f.key]) } as Partial<OnboardingDraft>);
            }}
            error={error !== null}
            helperText={error ?? f.hint}
          />
        );
      })}
    </Stack>
  );
}
